import { useState, useEffect, useCallback, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const TOUR_COMPLETED_KEY = 'settlewise-tour-completed';
const TOUR_TRIGGER_KEY = 'settlewise-show-tour';

interface TourStep {
  target: string;
  title: string;
  description: string;
}

const TOUR_STEPS: TourStep[] = [
  {
    target: '[data-tour="strategy"]',
    title: 'Your Strategy',
    description:
      'This is your personalized settlement strategy. Explore each section for detailed recommendations.',
  },
  {
    target: '[data-tour="ask-sage"]',
    title: 'Meet Sage',
    description:
      'Your AI divorce advisor. Ask anything about your case, finances, or next steps.',
  },
  {
    target: '[data-tour="documents"]',
    title: 'Document Intelligence',
    description:
      'Upload financial documents and let AI extract the important data automatically.',
  },
  {
    target: '[data-tour="net-worth"]',
    title: 'Financial Picture',
    description:
      'See your complete net worth with marital vs. separate property breakdown.',
  },
  {
    target: '[data-tour="support-calc"]',
    title: 'Support Calculator',
    description:
      "Estimate child support and alimony based on your state's formulas.",
  },
];

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function ProductTour() {
  const [active, setActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const rafRef = useRef<number>(0);

  // Check if tour should show
  useEffect(() => {
    const completed = localStorage.getItem(TOUR_COMPLETED_KEY);
    if (completed) return;

    const shouldShow = sessionStorage.getItem(TOUR_TRIGGER_KEY);
    if (shouldShow === 'true') {
      // Small delay to let the page settle
      const timer = setTimeout(() => {
        setActive(true);
        sessionStorage.removeItem(TOUR_TRIGGER_KEY);
      }, 800);
      return () => clearTimeout(timer);
    }

    // Poll for the trigger flag (in case it gets set after mount)
    const interval = setInterval(() => {
      const flag = sessionStorage.getItem(TOUR_TRIGGER_KEY);
      if (flag === 'true') {
        setActive(true);
        sessionStorage.removeItem(TOUR_TRIGGER_KEY);
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, []);

  const updatePosition = useCallback(() => {
    if (!active) return;

    const step = TOUR_STEPS[currentStep];
    const el = document.querySelector(step.target);
    if (el) {
      const rect = el.getBoundingClientRect();
      setTargetRect({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
    } else {
      setTargetRect(null);
    }

    rafRef.current = requestAnimationFrame(updatePosition);
  }, [active, currentStep]);

  useEffect(() => {
    if (active) {
      rafRef.current = requestAnimationFrame(updatePosition);
    }
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [active, updatePosition]);

  const completeTour = useCallback(() => {
    setActive(false);
    localStorage.setItem(TOUR_COMPLETED_KEY, 'true');
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  }, []);

  const handleNext = useCallback(() => {
    if (currentStep < TOUR_STEPS.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      completeTour();
    }
  }, [currentStep, completeTour]);

  const handleSkip = useCallback(() => {
    completeTour();
  }, [completeTour]);

  if (!active || !targetRect) return null;

  const step = TOUR_STEPS[currentStep];
  const isLastStep = currentStep === TOUR_STEPS.length - 1;
  const padding = 6;

  // Calculate tooltip position: to the right of the target, vertically centered
  const tooltipLeft = targetRect.left + targetRect.width + 16;
  const tooltipTop = targetRect.top + targetRect.height / 2;

  // If tooltip would go off-screen to the right, position it below instead
  const viewportWidth = window.innerWidth;
  const tooltipWidth = 320;
  const placeBelow = tooltipLeft + tooltipWidth > viewportWidth - 20;

  const tooltipStyle: React.CSSProperties = placeBelow
    ? {
        position: 'fixed',
        top: targetRect.top + targetRect.height + 12,
        left: Math.max(16, targetRect.left),
        width: tooltipWidth,
        zIndex: 10002,
      }
    : {
        position: 'fixed',
        top: tooltipTop,
        left: tooltipLeft,
        transform: 'translateY(-50%)',
        width: tooltipWidth,
        zIndex: 10002,
      };

  return (
    <>
      {/* Backdrop overlay with spotlight cutout using CSS clip-path */}
      <div
        className="fixed inset-0 z-[10000] pointer-events-none"
        style={{
          background: 'rgba(0, 0, 0, 0.5)',
          clipPath: `polygon(
            0% 0%,
            0% 100%,
            ${targetRect.left - padding}px 100%,
            ${targetRect.left - padding}px ${targetRect.top - padding}px,
            ${targetRect.left + targetRect.width + padding}px ${targetRect.top - padding}px,
            ${targetRect.left + targetRect.width + padding}px ${targetRect.top + targetRect.height + padding}px,
            ${targetRect.left - padding}px ${targetRect.top + targetRect.height + padding}px,
            ${targetRect.left - padding}px 100%,
            100% 100%,
            100% 0%
          )`,
        }}
      />

      {/* Spotlight border highlight */}
      <div
        className="fixed z-[10001] rounded-lg pointer-events-none"
        style={{
          top: targetRect.top - padding,
          left: targetRect.left - padding,
          width: targetRect.width + padding * 2,
          height: targetRect.height + padding * 2,
          boxShadow: '0 0 0 2px hsl(38 92% 50%), 0 0 12px 2px hsl(38 92% 50% / 0.3)',
        }}
      />

      {/* Tooltip */}
      <div
        style={tooltipStyle}
        className={cn(
          'bg-white rounded-xl shadow-2xl border border-slate-200 p-5',
          'animate-in fade-in-0 slide-in-from-left-2 duration-200'
        )}
      >
        {/* Step counter */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-medium text-slate-400 uppercase tracking-wide">
            {currentStep + 1} of {TOUR_STEPS.length}
          </span>
          {/* Progress dots */}
          <div className="flex gap-1.5">
            {TOUR_STEPS.map((_, i) => (
              <div
                key={i}
                className={cn(
                  'w-1.5 h-1.5 rounded-full transition-colors',
                  i === currentStep ? 'bg-amber-500' : i < currentStep ? 'bg-amber-300' : 'bg-slate-200'
                )}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <h3 className="text-base font-semibold text-slate-900 mb-1.5 font-display">
          {step.title}
        </h3>
        <p className="text-sm text-slate-600 leading-relaxed mb-4">
          {step.description}
        </p>

        {/* Actions */}
        <div className="flex items-center justify-between">
          <button
            onClick={handleSkip}
            className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            Skip tour
          </button>
          <Button
            size="sm"
            variant="accent"
            onClick={handleNext}
            className="px-4"
          >
            {isLastStep ? 'Done' : 'Next'}
          </Button>
        </div>
      </div>
    </>
  );
}

export default ProductTour;
